/**
 * ATTEST-INDEPENDENCE-MATRIX-1 (G.3) — what would block this letter, listed
 * before the Build button rather than after it.
 *
 * The wizard's honest position (`formState.ts`) is that the server decides. That
 * stays true here: nothing in this list refuses anything, nothing here disables
 * Build, and a clean list is never a promise that the letter will issue. What it
 * does is stop the CPA discovering on Step 5 that the thing to change was on
 * Step 3 — every row names its step and offers a jump to it.
 *
 * The rows are restricted to conditions that are decidable from the form alone:
 * a required field left empty, a statement written without a source, an
 * independence impairment. Anything needing server state — licence states,
 * assertion allow lists, assurance-phrase analysis, evidence *authenticity* —
 * is deliberately absent, and the footnote says so instead of letting silence
 * imply coverage.
 */
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import {
  previewEligibility,
  previewIndependence,
} from '@/components/attest/independencePreflight'
import { INDEPENDENCE_CODE_MEANINGS } from '@/components/attest/independenceHelp'
import type { AttestStepKey } from '@/components/attest/refusalGuidance'
import {
  engagementRequiresAttestEligibleIssuer,
  missingRequiredFields,
  stepLabel,
  type RequiredFieldContext,
} from '@/components/attest/requiredFields'
import { Button } from '@/components/ui/button'

export interface Blocker {
  id: string
  step: AttestStepKey
  stepLabel: string
  text: string
  /**
   * The DOM id of the control to land on, when one exists. Rows derived from
   * `missingRequiredFields` always have one — the entry id *is* the field's id
   * — so the jump can put the cursor in the box. Rows that describe a state
   * rather than a field (an impairment, an eligibility problem) leave it unset
   * and fall back to the step-level jump: a wrong target is worse than none.
   */
  fieldAnchor?: string
}

const DETAILS: Pick<Blocker, 'step' | 'stepLabel'> = {
  step: 'details', stepLabel: stepLabel('details'),
}
const INDEPENDENCE: Pick<Blocker, 'step' | 'stepLabel'> = {
  step: 'independence', stepLabel: stepLabel('independence'),
}

/**
 * Everything decidable from the form. Order follows the wizard's own order.
 *
 * The empty-field half of this list is no longer written here: it comes from
 * `missingRequiredFields`, the same derivation that decides which fields wear a
 * an asterisk (ATTEST-UX-1 D1). That is the point — a row in this list is
 * always a field the CPA has already been shown a star against, and a starred
 * field always shows up here when it is empty. Two hand-maintained lists would
 * disagree within a release.
 *
 * What is still written here is what is not an empty field: an independence
 * impairment, an eligibility problem, and the one case a per-field check cannot
 * see — a form where every statement row is blank.
 */
export function collectBlockers(ctx: RequiredFieldContext): Blocker[] {
  const { form, issuer, letterType, policy } = ctx

  const blockers: Blocker[] = missingRequiredFields(ctx).map(entry => ({
    id: entry.id,
    step: entry.step,
    stepLabel: stepLabel(entry.step),
    text: entry.missingText,
    fieldAnchor: entry.id,
  }))

  // Same filter `buildLetterRequest` applies — a wholly blank row is not a
  // statement, so its own fields stay dormant rather than flagging the empty
  // form the CPA just opened. But a letter with *no* statement at all is a
  // blocker, and no single field can say so.
  const hasStatement = form.statements.some(
    s => s.assertion_id.trim() || s.text.trim(),
  )
  if (!hasStatement) {
    blockers.push({
      ...DETAILS, id: 'no-statements',
      text: 'The letter makes no statements yet.',
      // Not a required-field row, but there is exactly one right place to
      // start: the first statement's assertion.
      fieldAnchor: 'assertion-0',
    })
  }

  const independence = previewIndependence(issuer, form.subjectEntity)
  if (independence.evaluated) {
    for (const row of independence.impairments) {
      blockers.push({
        ...INDEPENDENCE, id: `independence-${row.code}`,
        text: INDEPENDENCE_CODE_MEANINGS[row.code] ?? row.reason,
      })
    }
  }
  // PREFLIGHT-BASIS-AWARE-1: same derivation the Step-4 stars use, so a
  // blocker never appears here for an engagement type the server was never
  // going to check licence/peer-review on in the first place.
  const requiresEligibleIssuer = engagementRequiresAttestEligibleIssuer(letterType, policy)
  for (const problem of previewEligibility(issuer, requiresEligibleIssuer).problems) {
    blockers.push({
      ...INDEPENDENCE, id: `eligibility-${problem}`,
      text: `The firm is not eligible to issue: ${problem}.`,
    })
  }

  return blockers
}

export interface PreflightChecklistProps extends RequiredFieldContext {
  onGoToStep?: (step: AttestStepKey, fieldAnchor?: string) => void
}

export function PreflightChecklist({
  onGoToStep, ...ctx
}: PreflightChecklistProps) {
  const blockers = collectBlockers(ctx)

  if (blockers.length === 0) {
    return (
      <section
        data-testid="preflight"
        data-state="clear"
        className="rounded-lg border border-border p-4"
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          Nothing in the form blocks this letter
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          The binding checks — allowed assertions, assurance wording, licence
          states, evidence — run on the server when you build.
        </p>
      </section>
    )
  }

  return (
    <section
      data-testid="preflight"
      data-state="blocked"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4"
    >
      <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {blockers.length} thing{blockers.length === 1 ? '' : 's'} would block this letter
      </h3>
      <ul className="mt-3 space-y-2" data-testid="preflight-blockers">
        {blockers.map(b => (
          <li
            key={b.id}
            data-testid={`preflight-${b.id}`}
            // Stacked below `sm`. Side-by-side, the jump button's label
            // ("3. Letter and statements") is ~170px of intrinsic width and
            // never wraps, so at 375 it squeezes the blocker text into a
            // ~100px column six lines deep. `flex-wrap` does not rescue it:
            // `min-w-0` lets the text shrink indefinitely, so nothing ever
            // overflows enough to trigger a wrap — and nothing overflows the
            // page either, so the responsive harness passes it.
            className="flex flex-col items-start gap-1 text-sm text-amber-900 sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-between sm:gap-2"
          >
            <span className="min-w-0 flex-1">{b.text}</span>
            {onGoToStep ? (
              <Button
                variant="outline"
                size="sm"
                data-testid={`preflight-goto-${b.id}`}
                onClick={() => onGoToStep(b.step, b.fieldAnchor)}
              >
                {b.stepLabel}
              </Button>
            ) : (
              <span className="text-xs text-amber-800">{b.stepLabel}</span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-amber-800">
        Checked from the form only. You can still build — the server runs the
        checks that count and may refuse for reasons not visible here.
      </p>
    </section>
  )
}
